import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import type {
  PageConfigRequest,
  PageConfigResponse,
} from '@/types/api/page-config'

import { TagTypes } from '@/types/api/tag-types'

import { baseApiSlice } from './base-api-slice'
import {
  hasConfigsV2Source,
  isPageConfigsV2Enabled,
  readPageConfigV2,
  writePageConfigV2,
} from './page-config-v2'

const readsConfigsV2 = (id: string) =>
  isPageConfigsV2Enabled() && hasConfigsV2Source(id)

export const pageConfigSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPageConfig: builder.query<PageConfigResponse, string>({
      queryFn: async (id, _api, _extraOptions, baseQuery) => {
        if (readsConfigsV2(id)) return readPageConfigV2(id)
        const result = await baseQuery({
          url: `bff/page-config/${id}/`,
          method: 'GET',
        })
        return result.error
          ? { error: result.error as FetchBaseQueryError }
          : { data: result.data as PageConfigResponse }
      },
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, id) => [{ type: TagTypes.PageConfig, id }],
    }),
    updatePageConfig: builder.mutation<null, PageConfigRequest>({
      queryFn: async (
        { id, version, config },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        if (readsConfigsV2(id)) return writePageConfigV2(id, config)
        const result = await baseQuery({
          url: `bff/page-config/${id}/`,
          method: 'PUT',
          body: { version, config },
        })
        return result.error
          ? { error: result.error as FetchBaseQueryError }
          : { data: null }
      },
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.PageConfig, id },
      ],
    }),
  }),
})

export const {
  endpoints: { getPageConfig, updatePageConfig },
  useGetPageConfigQuery,
  useUpdatePageConfigMutation,
} = pageConfigSlice
